import React, {
  createContext,
  FC,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { PaymentPlan, PaymentInstallmentItem } from "@app/postBill/models";
import { CalculateInstallmentResponseData } from "../models";
import { LinkType, RedirectionMap } from "../models";
import { routes } from "@app/routing";
import { useLocation, useNavigate } from "react-router-dom";
import { getUpsLocalStorage } from "../utils/localStorage";

export interface UPSContextProps {
  upsAccessToken: string | null;
  setUPSAccessToken: (upsAccessToken: string) => void;
  upsPatientUid: string | null;
  setUPSPatientUid: (upsPatientUid: string) => void;
  upsTotalAmount: number;
  setUPSTotalAmount: (totalAmount: number) => void;
  selectedUPSPaymentPlan: PaymentPlan;
  setSelectedUPSPaymentPlan: (paymentPlan: PaymentPlan) => void;
  selectedUPSPaymentPlanItems: CalculateInstallmentResponseData[] | [];
  setSelectedUPSPaymentPlanItems: (
    paymentPlanItems: CalculateInstallmentResponseData[]
  ) => void;
  redirectionMap: RedirectionMap;
}

export const UPSContext = createContext<UPSContextProps>({
  upsAccessToken: null,
  setUPSAccessToken: () => undefined,
  upsPatientUid: null,
  setUPSPatientUid: () => undefined,
  upsTotalAmount: 0,
  setUPSTotalAmount: () => undefined,
  selectedUPSPaymentPlan: PaymentPlan.None,
  setSelectedUPSPaymentPlan: () => undefined,
  selectedUPSPaymentPlanItems: [],
  setSelectedUPSPaymentPlanItems: () => undefined,
  redirectionMap: {} as RedirectionMap,
});

interface UPSProviderProps {
  children: ReactNode;
}

const UPSProvider: FC<UPSProviderProps> = ({ children }) => {
  const [upsAccessToken, setUPSAccessToken] = useState<string | null>(null);
  const [upsPatientUid, setUPSPatientUid] = useState<string | null>(null);
  const [upsTotalAmount, setUPSTotalAmount] = useState<number>(0);
  const [selectedUPSPaymentPlan, setSelectedUPSPaymentPlan] =
    useState<PaymentPlan>(PaymentPlan.None);
  const [selectedUPSPaymentPlanItems, setSelectedUPSPaymentPlanItems] =
    useState<PaymentInstallmentItem[]>([]);

  const redirectionMap: RedirectionMap = {
    [LinkType.PAYMENT_REQUEST]: routes?.upsReviewInvoice,
    [LinkType.UPDATE_PAYMENT]: routes?.upsUpdatePaymentMethod,
    [LinkType.RECEIPT]: routes?.upsReceipt,
  };

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const authType = getUpsLocalStorage("authType");
  const guest_token = getUpsLocalStorage("guestToken");

  const allowedUPSRoutes = useMemo(
    () => [
      routes.upsUpdatePaymentMethod,
      routes.upsUpdatePaymentMethodSuccess,
      routes.upsUpdatePaymentMethodFailed,
      routes.upsReviewInvoice,
      routes.upsCreatePaymentMethod,
      routes.upsCreatePaymentConfirmation,
      routes.upsCreatePaymentFailure,
      routes.upsReceipt,
    ],
    []
  );

  useEffect(() => {
    const isComingFromAllowedRoute = allowedUPSRoutes.includes(pathname);

    if (
      isComingFromAllowedRoute &&
      !upsPatientUid &&
      authType === "guest" &&
      guest_token
    ) {
      navigate(`${routes.upsGuestLogin}?guest_token=${guest_token}`);
    }
  }, [upsPatientUid, guest_token, allowedUPSRoutes, pathname]);

  return (
    <UPSContext.Provider
      value={{
        upsAccessToken,
        setUPSAccessToken,
        upsPatientUid,
        setUPSPatientUid,
        upsTotalAmount,
        setUPSTotalAmount,
        selectedUPSPaymentPlan,
        setSelectedUPSPaymentPlan,
        selectedUPSPaymentPlanItems,
        setSelectedUPSPaymentPlanItems,
        redirectionMap,
      }}
    >
      {children}
    </UPSContext.Provider>
  );
};

export default UPSProvider;
